import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../../../parts/SvgIcon";
import { PasteMealComponent } from "../../../../../parts/PasteMealComponent";

/**
 * EmptyMealState
 *
 * Shared component to display an empty meal state with "Add Food" button
 * Used in both detailed and simplified meal planners
 *
 * If there's content in the clipboard (localStorage), shows PasteMealComponent
 */
export const EmptyMealState = ({
  date,
  mealType,
  disabled = false,
  isDropTarget = false,
  dayType,
  optionId,
  query,
  meal,
  day,
  planMode,
  week,
}) => {
  const { t } = useTranslation("athlete-portal");
  const hasClipboardData =
    typeof window !== "undefined" &&
    (() => {
      try {
        const m = window.localStorage.getItem("mealsToCopy");
        const meta = window.localStorage.getItem("mealCopyMetadata");
        return !!(m && meta);
      } catch {
        return false;
      }
    })();

  if (isDropTarget) return null;
  if (disabled) return null;

  // Use manual string construction to preserve spaces in date (URLSearchParams converts spaces to +)
  let href = `/actions/refresh?modal[type]=create-meal-planner-food&mealPlannerDate=${encodeURIComponent(date)}&mealPlannerMeal=${encodeURIComponent(mealType)}`;

  // Include dayType and optionId if present (for simplified meal plans)
  if (dayType && optionId) {
    href += `&dayType=${encodeURIComponent(dayType)}&optionId=${encodeURIComponent(optionId.toString())}`;
  }

  // If there's content in clipboard, show both PasteMealComponent and "Add Food" button
  if (hasClipboardData && query && meal && day) {
    return (
      <div className="has-clipboard">
        <a href={href} className="day-link">
          <SvgIcon src="/img/new-icons/add-square-outline.svg" />
          <span>{t("emptyMealState.addFood")}</span>
        </a>
        <PasteMealComponent
          meal={meal}
          day={day}
          query={query}
          planMode={planMode}
          week={week}
          classNames="day-link"
          withFullInfo={true}
          hasFoods={false}
        />
      </div>
    );
  }

  // If no clipboard content, show only "Add Food" button
  return (
    <a href={href} className="day-link">
      <SvgIcon src="/img/new-icons/add-square-outline.svg" />
      <span>{t("emptyMealState.addFood")}</span>
    </a>
  );
};

EmptyMealState.propTypes = {
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  mealType: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  isDropTarget: PropTypes.bool, // true when drag is over this cell (hides Add Food / Paste so drop zone is clear)
  dayType: PropTypes.string, // "training" or "rest" for simplified meal plans
  optionId: PropTypes.string, // Stable ID of the option for simplified meal plans
  query: PropTypes.object, // Query object with mealPlan and athlete
  meal: PropTypes.object, // Meal object with type, foods, etc.
  day: PropTypes.object, // Day object with date, dayType, optionId
  planMode: PropTypes.string, // "detailed" or "simplified"
  week: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), // Week number for detailed mode paste
};
